<template>
  <PaginatedResource
    v-model="responses"
    :action="xhrAction"
    :params="responsesResourceParams"
    ref="responsesResource"
    id-key="id"
    v-slot="{ loadingPage }"
  >
    <WithSpinnerPlaceholder :loading="loadingPage === 1" :size="26">
      <div v-if="loadingPage === 1" class="vacancy-show__placeholder-height" />
      <WithLoadMore>
        <SeparatedList
          v-if="responses"
          gap="medium-large"
          :items="responses.list"
          :get-key="(_item) => _item.id"
          v-slot="{ item }"
        >
          <VacancyResponse :response="item">
            <template #controls>
              <div class="response__controls">
                <JobInviteButton
                  v-if="item.jobInviteButton"
                  :job-invite-button="item.jobInviteButton"
                  invited-from="vacancy"
                  :company-id="companyId"
                  @success="makeAsInvited(item)"
                />
                <div class="response__controls-wrapper">
                  <div class="response__controls-group">
                    <BasicButton
                      title="Написать соискателю"
                      appearance="icon-only"
                      :href="`/responses/${item.id}/connect`"
                    >
                      <SvgIcon
                        icon="message"
                        size="24"
                        :source="$options.icons"
                      />
                    </BasicButton>

                    <ResponseDownloadButton
                      v-if="!item.archived"
                      :vacancy-id="vacancyId"
                      :response-id="item.id"
                    />

                    <ResponseFavouriteButton
                      :vacancy-id="vacancyId"
                      :response-id="item.id"
                      :favourite="item.favorite"
                      @favouriteResponse="$emit('changeFavouritesCount', 1)"
                      @unfavouriteResponse="$emit('changeFavouritesCount', -1)"
                    />

                    <ResponseAnalyticsButton
                      v-if="item.hasAnalytics"
                      :vacancy-id="vacancyId"
                      :response-id="item.id"
                      @responseAnalytics="storeAnalytics"
                    />
                  </div>

                  <ResponseDeleteButton
                    :vacancy-id="vacancyId"
                    :response-id="item.id"
                    :archived="item.archived"
                    @archiveResponse="
                      (e) => {
                        removeResponse(e);
                        $emit('changeArchivedCount', 1);
                      }
                    "
                    @unarchiveResponse="
                      (e) => {
                        removeResponse(e);
                        $emit('changeArchivedCount', -1);
                      }
                    "
                  />
                </div>

                <div v-if="false"></div>
              </div>
            </template>
            <template v-if="analytics[item.id]" #footer>
              <div class="response-analytics">
                <h5 class="response-analytics__title">
                  Путь соискателя до отклика
                </h5>
                <div
                  v-if="analytics[item.id].path"
                  class="response-analytics__path"
                  v-html="analytics[item.id].path"
                />

                <div
                  v-if="analytics[item.id].details"
                  class="response-analytics__table-wrapper"
                >
                  <table>
                    <thead>
                      <tr>
                        <th>Источник (кампания)</th>
                        <th>Referer</th>
                        <th>utm_medium</th>
                        <th>utm_campaign</th>
                        <th>utm_source</th>
                        <th>utm_content</th>
                        <th>f</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(source, index) in analytics[item.id].details"
                        :key="index"
                      >
                        <td v-text="source.source" />
                        <td v-text="source.referer" />
                        <td v-text="source.utm_medium" />
                        <td v-text="source.utm_campaign" />
                        <td v-text="source.utm_source" />
                        <td v-text="source.utm_content" />
                        <td v-text="source.f" />
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </VacancyResponse>
        </SeparatedList>
      </WithLoadMore>
    </WithSpinnerPlaceholder>
  </PaginatedResource>
</template>

<script>
import { VacanciesResponsesResource } from 'src/services/vacancies.js';

import BasicButton from 'src/components/BasicButton/BasicButton.vue';
import SvgIcon from 'src/components/icons/SvgIcon/SvgIcon.vue';
import JobInviteButton from 'src/components/JobInviteButton/JobInviteButton.vue';
import PaginatedResource from 'src/components/pagination/PaginatedResource/PaginatedResource.vue';
import WithLoadMore from 'src/components/pagination/WithLoadMore/WithLoadMore.vue';
import WithSpinnerPlaceholder from 'src/components/placeholders/WithSpinnerPlaceholder/WithSpinnerPlaceholder.vue';
import ResponseAnalyticsButton from 'src/components/responses/ResponseAnalyticsButton/ResponseAnalyticsButton.vue';
import ResponseDeleteButton from 'src/components/responses/ResponseDeleteButton/ResponseDeleteButton.vue';
import ResponseDownloadButton from 'src/components/responses/ResponseDownloadButton/ResponseDownloadButton.vue';
import ResponseFavouriteButton from 'src/components/responses/ResponseFavouriteButton/ResponseFavouriteButton.vue';
import VacancyResponse from 'src/components/responses/VacancyResponse/VacancyResponse.vue';
import SeparatedList from 'src/components/SeparatedList/SeparatedList.vue';

export default {
  name: 'VacancyResponses',
  components: {
    BasicButton,
    WithSpinnerPlaceholder,
    WithLoadMore,
    PaginatedResource,
    SeparatedList,
    VacancyResponse,
    ResponseAnalyticsButton,
    ResponseDownloadButton,
    ResponseFavouriteButton,
    ResponseDeleteButton,
    SvgIcon,
    JobInviteButton,
  },

  icons: '/images/responses-v3.svg',

  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
    group: {
      type: String,
      default: 'vacancy_responses_all_count',
    },
    companyId: {
      type: String,
      required: true,
    },
  },

  emits: ['changeArchivedCount', 'changeFavouritesCount'],

  provide() {
    return {
      replaceDefaultJobInviteParams: this.replaceDefaultJobInviteParams,
    };
  },

  data() {
    return {
      page: 1,
      responses: null,
      analytics: {},
    };
  },

  mounted() {
    this.$refs.responsesResource.loadAndReplace();
  },

  methods: {
    responsesResourceGetAll: VacanciesResponsesResource.all.get,
    responsesResourceGetFavorited: VacanciesResponsesResource.favorited.get,
    responsesResourceGetArchived: VacanciesResponsesResource.archived.get,

    replaceDefaultJobInviteParams({ vacancy, message }) {
      if (!Array.isArray(this.responses.list)) return;

      this.responses.list = this.responses.list.map((response) => {
        if (!response.jobInviteButton) return response;

        return {
          ...response,
          jobInviteButton: {
            ...response.jobInviteButton,
            vacancy,
            message,
          },
        };
      });
    },

    storeAnalytics(e) {
      this.analytics = {
        ...this.analytics,
        [e.responseId]: e.data,
      };
    },

    removeResponse(e) {
      this.responses = {
        ...this.responses,
        list: this.responses.list.filter(
          (response) => response.id !== e.responseId,
        ),
      };
    },

    makeAsInvited(response) {
      // eslint-disable-next-line
      response.jobInviteButton.state = 'already_sent';
    },
  },

  computed: {
    responsesResourceParams() {
      return {
        vacancyId: `${this.vacancyId}`,
      };
    },
    xhrAction() {
      if (this.group === 'vacancy_responses_favorited_count')
        return this.responsesResourceGetFavorited;
      if (this.group === 'vacancy_responses_archived_count')
        return this.responsesResourceGetArchived;
      return this.responsesResourceGetAll;
    },
  },
};
</script>
